import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button, Descriptions, Drawer, Input, Modal, Select, Space, Spin, Tag, message
} from 'antd';
import { SearchOutlined, EyeOutlined, FileImageOutlined, EditOutlined, LinkOutlined } from '@ant-design/icons';
import moment from 'moment';
import InsuranceEnrollmentService from '../../services/InsuranceEnrollmentService';
import { PageContainer, StatsRow, StatCard } from '../Surveys/styled';
import { BookingTable } from '../EventManagement/NewStyled';
import useResponsive from '../../hooks/useResponsive';
import MobileCardList from '../../components/Responsive/MobileCardList';
import ResponsiveFilterBar from '../../components/Responsive/ResponsiveFilterBar';
import { BENEFIT_CONSULTANTS, STATUS_OPTIONS } from '../InsuranceEnrollment/constants';

const STATUS = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s]));
const RANGES = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 0, label: 'All time' }
];

const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : '—');
const formatPhone = (p = '') => (p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : p);
const kindSlug = (title = '') => title.toLowerCase().replace(/\s+/g, '-');

const StatusTag = ({ value }) => <Tag color={STATUS[value]?.color}>{STATUS[value]?.label || value}</Tag>;

const EnrollmentDrawer = ({ id, onClose, onChanged }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!id) return;
    setRecord(null);
    setRevealed(null);
    setLoading(true);
    InsuranceEnrollmentService.get(id).then((res) => {
      if (res.success) {
        setRecord(res.data);
        setNotes(res.data.staffNotes || '');
      } else {
        message.error(res.error);
      }
      setLoading(false);
    });
  }, [id]);

  const reveal = () => {
    Modal.confirm({
      title: 'Show full Social Security Numbers?',
      content: 'This is recorded in the audit log with your name and time.',
      okText: 'Show',
      onOk: async () => {
        const res = await InsuranceEnrollmentService.revealSsn(id);
        if (res.success) setRevealed(res.data);
        else message.error(res.error);
      }
    });
  };

  const openFile = async (kind) => {
    const res = await InsuranceEnrollmentService.file(id, kind);
    if (!res.success) {
      message.error(res.error);
      return;
    }
    setFile({ url: URL.createObjectURL(res.data), type: res.data.type, title: kind === 'signature' ? 'Signature' : 'Photo ID' });
  };

  const closeFile = () => {
    if (file) URL.revokeObjectURL(file.url);
    setFile(null);
  };

  const save = async (patch) => {
    setSaving(true);
    const res = await InsuranceEnrollmentService.update(id, patch);
    setSaving(false);
    if (res.success) {
      setRecord(res.data);
      message.success('Saved');
      onChanged();
    } else {
      message.error(res.error);
    }
  };

  const r = record;
  return (
    <Drawer
      open={Boolean(id)}
      onClose={onClose}
      width={Math.min(720, window.innerWidth)}
      title={r ? `${r.name} · ${r.referenceCode}` : 'Enrollment'}
      destroyOnClose
    >
      {loading || !r ? <Spin /> : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space wrap>
            <Select
              value={r.status}
              onChange={(status) => save({ status })}
              options={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
              style={{ width: 180 }}
              disabled={saving}
            />
            <Button icon={<FileImageOutlined />} onClick={() => openFile('photo-id')} disabled={!r.hasPhotoId}>Photo ID</Button>
            <Button icon={<EditOutlined />} onClick={() => openFile('signature')} disabled={!r.hasSignature}>Signature</Button>
            <Button icon={<EyeOutlined />} onClick={reveal} disabled={Boolean(revealed)}>Show SSNs</Button>
          </Space>

          <Descriptions bordered size="small" column={1} title="Client">
            <Descriptions.Item label="Name">{r.name}</Descriptions.Item>
            <Descriptions.Item label="Date of birth">{r.dateOfBirth}</Descriptions.Item>
            <Descriptions.Item label="SSN">{revealed?.ssn || r.ssnMasked}</Descriptions.Item>
            <Descriptions.Item label="Gender">{r.gender}</Descriptions.Item>
            <Descriptions.Item label="Address">
              {[r.address.street1, r.address.street2].filter(Boolean).join(', ')}<br />
              {r.address.city}, {r.address.state} {r.address.zip}
            </Descriptions.Item>
            <Descriptions.Item label="Phone"><a href={`tel:${r.phone}`}>{formatPhone(r.phone)}</a></Descriptions.Item>
            <Descriptions.Item label="Email"><a href={`mailto:${r.email}`}>{r.email}</a></Descriptions.Item>
            {r.officeEmail && <Descriptions.Item label="Office email">{r.officeEmail}</Descriptions.Item>}
            <Descriptions.Item label="Occupation">{r.occupation}</Descriptions.Item>
            <Descriptions.Item label="Monthly income">{r.monthlyIncome}</Descriptions.Item>
            {r.doctorOrClinic && <Descriptions.Item label="Doctor or clinic">{r.doctorOrClinic}</Descriptions.Item>}
          </Descriptions>

          <Descriptions bordered size="small" column={1} title="Household">
            <Descriptions.Item label="Household size">{r.householdSize.join(', ')}</Descriptions.Item>
            {r.dependents.map((d, i) => (
              <Descriptions.Item key={i} label={d.relationship}>
                {d.firstName} {d.lastName} · DOB {d.dateOfBirth} · SSN {revealed?.dependents?.[i]?.ssn || d.ssnMasked || 'not provided'}
              </Descriptions.Item>
            ))}
            {r.notes && <Descriptions.Item label="Notes"><span style={{ whiteSpace: 'pre-wrap' }}>{r.notes}</span></Descriptions.Item>}
          </Descriptions>

          <Descriptions bordered size="small" column={1} title="Coverage">
            <Descriptions.Item label="Benefit consultant">{r.benefitConsultant}</Descriptions.Item>
            <Descriptions.Item label="Carriers">{r.insuranceSelections.join(', ')}</Descriptions.Item>
            <Descriptions.Item label="Dental, vision, or hearing">{r.dentalVisionHearing}</Descriptions.Item>
            <Descriptions.Item label="Life insurance">{r.lifeInsurance}</Descriptions.Item>
            <Descriptions.Item label="Insurance enrolled">{r.insuranceEnrolledName}</Descriptions.Item>
            <Descriptions.Item label="Enrollment location">{r.enrollmentLocation}</Descriptions.Item>
            <Descriptions.Item label="Consent signed">{r.consent?.signedAt ? moment(r.consent.signedAt).format('MMM D, YYYY h:mm A') : '—'}</Descriptions.Item>
            <Descriptions.Item label="Source">
              {r.source === 'sms' ? <Tag icon={<LinkOutlined />} color="cyan">Text message</Tag> : <Tag>Direct</Tag>}
              {r.patientId && <span style={{ fontSize: 12, color: '#8c8c8c' }}>Member {r.patientId}</span>}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Staff notes</div>
            <Input.TextArea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={5000} />
            <Button style={{ marginTop: 8 }} onClick={() => save({ staffNotes: notes })} loading={saving}>Save notes</Button>
          </div>
        </Space>
      )}
      <Modal open={Boolean(file)} onCancel={closeFile} footer={null} title={file?.title} width={720} destroyOnClose>
        {file && (file.type.startsWith('image/') && !/hei[cf]/.test(file.type)
          ? <img src={file.url} alt={file.title} style={{ maxWidth: '100%' }} />
          : (
            <Space>
              <a href={file.url} target="_blank" rel="noreferrer">Open file</a>
              <a href={file.url} download={`${r?.referenceCode || 'enrollment'}-${kindSlug(file.title)}`}>Download</a>
            </Space>
          ))}
      </Modal>
    </Drawer>
  );
};

const InsuranceEnrollmentsPage = () => {
  const { isMobile } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState();
  const [consultant, setConsultant] = useState();
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [openId, setOpenId] = useState(() => searchParams.get('id'));

  const closeDrawer = () => {
    setOpenId(null);
    if (searchParams.has('id')) setSearchParams({}, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await InsuranceEnrollmentService.list({
      page: pagination.current,
      limit: pagination.pageSize,
      q: query,
      status,
      consultant
    });
    if (res.success) {
      setItems(res.data.items);
      setPagination((p) => ({ ...p, total: res.data.total }));
    } else {
      message.error(res.error);
    }
    setLoading(false);
  }, [pagination.current, pagination.pageSize, query, status, consultant]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStats = useCallback(async () => {
    const res = await InsuranceEnrollmentService.stats(days);
    if (res.success) setStats(res.data);
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const resetPage = (setter) => (v) => {
    setter(v);
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name, r) => (
        <div>
          <strong>{name}</strong>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.referenceCode}</div>
        </div>
      )
    },
    { title: 'Phone', dataIndex: 'phone', width: 150, render: formatPhone },
    { title: 'Benefit Consultant', dataIndex: 'benefitConsultant', width: 200 },
    { title: 'Location', dataIndex: 'enrollmentLocation', width: 200 },
    {
      title: 'Source',
      dataIndex: 'source',
      width: 110,
      render: (s) => (s === 'sms' ? <Tag color="cyan">Text</Tag> : <Tag>Direct</Tag>)
    },
    { title: 'Status', dataIndex: 'status', width: 130, render: (v) => <StatusTag value={v} /> },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      width: 170,
      render: (d) => moment(d).format('MMM D, YYYY h:mm A')
    }
  ];

  const sms = stats?.sms || {};

  return (
    <PageContainer>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Select value={days} onChange={setDays} options={RANGES} style={{ width: 160 }} />
      </div>
      <StatsRow>
        <StatCard>
          <div className="label">Texts sent</div>
          <div className="value">{sms.textsSent ?? '—'}</div>
        </StatCard>
        <StatCard>
          <div className="label">Link clicked</div>
          <div className="value">{sms.linksClicked ?? '—'}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{pct(sms.linksClicked, sms.textsSent)} of texts</div>
        </StatCard>
        <StatCard>
          <div className="label">Page views</div>
          <div className="value">{stats?.pageViews ?? '—'}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{stats ? `${stats.uniqueVisitors} unique · ${stats.directViews} direct` : ''}</div>
        </StatCard>
        <StatCard>
          <div className="label">Submissions</div>
          <div className="value">{stats?.submissions ?? '—'}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            {stats ? `${stats.smsSubmissions} from texts · ${pct(sms.linksSubmitted, sms.textsSent)} of texts` : ''}
          </div>
        </StatCard>
      </StatsRow>

      <ResponsiveFilterBar
        activeFilterCount={[status, consultant].filter(Boolean).length}
        search={(
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search name, phone, email, or INS- number"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, current: 1 })); }}
            style={{ width: 320, height: 40 }}
            allowClear
          />
        )}
        filters={[
          <Select
            key="status"
            placeholder="Status"
            style={{ width: 160, height: 40 }}
            allowClear
            value={status}
            onChange={resetPage(setStatus)}
            options={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
          />,
          <Select
            key="consultant"
            placeholder="Benefit consultant"
            style={{ width: 240, height: 40 }}
            allowClear
            showSearch
            value={consultant}
            onChange={resetPage(setConsultant)}
            options={BENEFIT_CONSULTANTS.map((c) => ({ value: c, label: c }))}
          />
        ]}
      />

      {isMobile ? (
        <MobileCardList
          records={items}
          rowKey="_id"
          loading={loading}
          onRowClick={(r) => setOpenId(r._id)}
          emptyText="No enrollments yet"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize }))
          }}
          renderCard={(r) => (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <strong>{r.name}</strong>
                <StatusTag value={r.status} />
              </div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                {r.referenceCode} · {formatPhone(r.phone)}
              </div>
              <div style={{ fontSize: 12, marginTop: 6 }}>{r.benefitConsultant}</div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
                {moment(r.createdAt).format('MMM D, YYYY h:mm A')} · {r.source === 'sms' ? 'Text' : 'Direct'}
              </div>
            </div>
          )}
        />
      ) : (
        <BookingTable
          rowKey="_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          scroll={{ x: 800 }}
          onRow={(r) => ({ onClick: () => setOpenId(r._id), style: { cursor: 'pointer' } })}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} enrollments`,
            className: 'event-management-pagination',
            onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize }))
          }}
        />
      )}

      <EnrollmentDrawer
        id={openId}
        onClose={closeDrawer}
        onChanged={() => { load(); loadStats(); }}
      />
    </PageContainer>
  );
};

export default InsuranceEnrollmentsPage;
